import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser]       = useState(null);
    const [loading, setLoading] = useState(true);

    // Check stored session on mount
    useEffect(() => {
        const stored = localStorage.getItem('zyco_user');
        if (stored) {
            try {
                setUser(JSON.parse(stored));
            } catch (e) {
                localStorage.removeItem('zyco_user');
                localStorage.removeItem('zyco_token');
            }
        }
        setLoading(false);
    }, []);

    const applySession = useCallback((data) => {
        const userData = data.user;
        const token    = data.token;

        localStorage.setItem('zyco_token', token);
        localStorage.setItem('zyco_user', JSON.stringify(userData));
        setUser(userData);

        return userData;
    }, []);

    const login = useCallback(async (email, password) => {
        const res = await authAPI.login(email, password);
        const data = res.data;

        if (data.error === '1') {
            throw new Error(data.errors?.[0]?.Key || data.message || 'Login failed');
        }

        return applySession(data);
    }, [applySession]);

    // Passwordless session for a known user_id — used by the /autologin
    // route (e.g. launched from the desktop app). Backend enforces the
    // actual security gate (User-Agent must contain "zyco"); this just
    // wires the resulting token/user into the same session storage login()
    // uses.
    const autologin = useCallback(async (userId) => {
        let data;
        try {
            data = (await authAPI.autologin(userId)).data;
        } catch (err) {
            // A denied request (e.g. the User-Agent gate) comes back as a
            // non-2xx status, so it lands here rather than in the
            // data.error === '1' branch below — surface the server's own
            // message instead of axios's generic "Request failed with
            // status code ###".
            data = err.response?.data;
            if (!data) throw err;
        }

        if (data.error === '1') {
            throw new Error(data.errors?.[0]?.Key || data.message || 'Auto login failed');
        }

        return applySession(data);
    }, [applySession]);

    const logout = useCallback(async () => {
        try { await authAPI.logout(); } catch (e) { /* ignore */ }
        localStorage.removeItem('zyco_token');
        localStorage.removeItem('zyco_user');
        setUser(null);
    }, []);

    // Resolve the access_level ('self' | 'teams' | 'units' | 'n/a') for a
    // dotted feature key, e.g. 'dashboard', 'reports.attendance',
    // 'settings.users'. Mirrors the API's own resolution exactly (confirmed
    // against the real role editor's Roles/Create.vue — there is no 'all' in
    // real data; 'n/a' explicitly means not authorized), so the UI and the
    // data it requests never disagree about scope. Admins get 'all'
    // (unrestricted); a feature with no rule set defaults to 'n/a' (deny —
    // fail closed, not fail open).
    const accessLevel = useCallback((featureKey) => {
        if (!user) return 'n/a';
        if (user.role === 'admin') return 'all';
        let node = user.priviliges;
        for (const part of featureKey.split('.')) {
            node = node?.[part];
            if (!node) return 'n/a';
        }
        return node.access_level || 'n/a';
    }, [user]);

    const value = {
        user,
        loading,
        login,
        autologin,
        logout,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        accessLevel,
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
    return ctx;
}

export default AuthContext;
